import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

/**
 * Backend tool name -> the "what's happening" label. Covers the agent loop's
 * tool registry and the v1 MC-gate's tools; both emit the same
 * `chat.completion.tool_status` shape with different tool names.
 *
 * Client-executed tools (`highlight_ui`, `route_file`, `open_*`, the
 * questionnaire writes) fire these too. They emit a lifecycle event alongside
 * their `chat.completion.action` because the frontend splits a turn's text on
 * those events — without one, a turn whose only tool was `highlight_ui`
 * arrived as a single glued paragraph.
 */
const TOOL_LABEL_KEYS: Record<string, TranslationKeys> = {
  list_article_compliance: "AI_CHAT/TOOL_STATUS/MC_LOOKUP",
  get_article_details: "AI_CHAT/TOOL_STATUS/MC_LOOKUP",
  list_supplier_inquiries: "AI_CHAT/TOOL_STATUS/MC_LOOKUP",
  get_mc_overview_stats: "AI_CHAT/TOOL_STATUS/MC_LOOKUP",
  list_articles_containing_substance: "AI_CHAT/TOOL_STATUS/MC_LOOKUP",
  list_article_substances: "AI_CHAT/TOOL_STATUS/MC_LOOKUP",
  get_regulation_info: "AI_CHAT/TOOL_STATUS/MC_LOOKUP",
  list_articles_citing_exemption: "AI_CHAT/TOOL_STATUS/MC_LOOKUP",
  rag_search: "AI_CHAT/TOOL_STATUS/RAG_SEARCH",
  web_search: "AI_CHAT/TOOL_STATUS/WEB_SEARCH",
  highlight_ui: "AI_CHAT/TOOL_STATUS/HIGHLIGHT_UI",
  read_file: "AI_CHAT/TOOL_STATUS/READ_FILE",
  classify_file: "AI_CHAT/TOOL_STATUS/READ_FILE",
  route_file: "AI_CHAT/TOOL_STATUS/UPLOAD_FILE",
  list_kb_documents: "AI_CHAT/TOOL_STATUS/KB_DOCUMENTS",
  get_questionnaire_answers: "AI_CHAT/TOOL_STATUS/QUESTIONNAIRE",
  draft_or_revise_answers: "AI_CHAT/TOOL_STATUS/QUESTIONNAIRE",
  open_questionnaire: "AI_CHAT/TOOL_STATUS/QUESTIONNAIRE",
  list_questionnaires: "AI_CHAT/TOOL_STATUS/HISTORY",
  find_past_answers: "AI_CHAT/TOOL_STATUS/HISTORY",
  reuse_past_answer: "AI_CHAT/TOOL_STATUS/HISTORY",
  list_supplier_declarations: "AI_CHAT/TOOL_STATUS/DECLARATIONS",
  get_supplier_declaration: "AI_CHAT/TOOL_STATUS/DECLARATIONS",
  list_suppliers_by_declaration_coverage: "AI_CHAT/TOOL_STATUS/DECLARATIONS",
  get_customer_studio_overview: "AI_CHAT/TOOL_STATUS/CUSTOMER_STUDIO",
  list_customer_requests: "AI_CHAT/TOOL_STATUS/CUSTOMER_STUDIO",
  get_customer_request: "AI_CHAT/TOOL_STATUS/CUSTOMER_STUDIO",
  list_customer_request_gaps: "AI_CHAT/TOOL_STATUS/CUSTOMER_STUDIO",
  open_customer_request: "AI_CHAT/TOOL_STATUS/CUSTOMER_STUDIO",
  list_conformity_declarations: "AI_CHAT/TOOL_STATUS/CONFORMITY",
  get_conformity_declaration: "AI_CHAT/TOOL_STATUS/CONFORMITY",
  open_conformity_declaration: "AI_CHAT/TOOL_STATUS/CONFORMITY",
  list_mc_imports: "AI_CHAT/TOOL_STATUS/IMPORTS",
  get_mc_import: "AI_CHAT/TOOL_STATUS/IMPORTS",
  list_mc_import_columns: "AI_CHAT/TOOL_STATUS/IMPORTS",
  get_supplier_inquiry: "AI_CHAT/TOOL_STATUS/INQUIRIES",
  open_inquiry_draft: "AI_CHAT/TOOL_STATUS/INQUIRIES",
  get_inquiry_template_settings: "AI_CHAT/TOOL_STATUS/INQUIRY_SETTINGS",
  create_supplier_inquiries: "AI_CHAT/TOOL_STATUS/INQUIRY_CREATE",
  update_inquiry_draft: "AI_CHAT/TOOL_STATUS/INQUIRY_UPDATE",
  set_inquiry_auto_reminders: "AI_CHAT/TOOL_STATUS/INQUIRY_UPDATE",
  close_inquiry: "AI_CHAT/TOOL_STATUS/INQUIRY_UPDATE",
  open_inquiry_reminder: "AI_CHAT/TOOL_STATUS/INQUIRY_REMINDER_OPEN",
  save_inquiry_template_settings: "AI_CHAT/TOOL_STATUS/INQUIRY_SETTINGS_SAVE",
  load_tools: "AI_CHAT/TOOL_STATUS/LOAD_TOOLS",
  open_inquiry: "AI_CHAT/TOOL_STATUS/INQUIRIES",
  list_conformity_frameworks: "AI_CHAT/TOOL_STATUS/CONFORMITY",
  cancel_mc_import: "AI_CHAT/TOOL_STATUS/IMPORTS",
  open_mc_import: "AI_CHAT/TOOL_STATUS/IMPORTS",
  get_declaration_review: "AI_CHAT/TOOL_STATUS/DECLARATIONS",
  get_declaration_scope: "AI_CHAT/TOOL_STATUS/DECLARATIONS",
  open_declaration_review: "AI_CHAT/TOOL_STATUS/DECLARATIONS",
  // Writes against one declaration — assign a supplier, override a verdict,
  // remap a cited part number, park/unpark a review item, retry extraction.
  assign_declaration_supplier: "AI_CHAT/TOOL_STATUS/DECLARATION_UPDATE",
  record_declaration_override: "AI_CHAT/TOOL_STATUS/DECLARATION_UPDATE",
  remap_declaration_article: "AI_CHAT/TOOL_STATUS/DECLARATION_UPDATE",
  retire_declaration_override: "AI_CHAT/TOOL_STATUS/DECLARATION_UPDATE",
  retry_declaration_extraction: "AI_CHAT/TOOL_STATUS/DECLARATION_UPDATE",
  get_company_information: "AI_CHAT/TOOL_STATUS/COMPANY_PROFILE",
  // Reads against the supplier/article/product/customer/substance catalogue.
  get_master_data_audit: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  get_master_data_overview: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  get_master_data_record: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  list_articles: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  list_customers: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  list_products: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  list_substances: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  list_suppliers: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  open_master_data_record: "AI_CHAT/TOOL_STATUS/MASTER_DATA",
  // Writes against that same catalogue.
  create_customer: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
  link_article_suppliers: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
  override_article_compliance: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
  unlink_article_supplier: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
  update_article: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
  update_article_supplier_link: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
  update_customer: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
  update_product: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
  update_supplier: "AI_CHAT/TOOL_STATUS/MASTER_DATA_UPDATE",
}

/**
 * Raw resource-name (the domain-named list key a tool result carries, e.g.
 * `declarations`) -> its own translation key, the same key-with-fallback
 * shape `TOOL_LABEL_KEYS` already uses for tool names. Every entry here is a
 * list key actually returned by a tool reachable from this chat's registry
 * (`build_default_registry` in `builtin_tools.py`) — verified by reading each
 * handler's result construction, not guessed from the name. A resource this
 * build does not recognise (a new list key on a new tool) falls back to
 * `humanizeKey`, same as an unmapped tool falls back to `humanizeToolName`.
 */
const RESOURCE_LABEL_KEYS: Record<string, TranslationKeys> = {
  declarations: "AI_CHAT/TOOL_RESOURCE/DECLARATIONS",
  questionnaires: "AI_CHAT/TOOL_RESOURCE/QUESTIONNAIRES",
  items: "AI_CHAT/TOOL_RESOURCE/ITEMS",
  articles: "AI_CHAT/TOOL_RESOURCE/ARTICLES",
  suppliers: "AI_CHAT/TOOL_RESOURCE/SUPPLIERS",
  customers: "AI_CHAT/TOOL_RESOURCE/CUSTOMERS",
  inquiries: "AI_CHAT/TOOL_RESOURCE/INQUIRIES",
  imports: "AI_CHAT/TOOL_RESOURCE/IMPORTS",
  substances: "AI_CHAT/TOOL_RESOURCE/SUBSTANCES",
  products: "AI_CHAT/TOOL_RESOURCE/PRODUCTS",
  requests: "AI_CHAT/TOOL_RESOURCE/REQUESTS",
  frameworks: "AI_CHAT/TOOL_RESOURCE/FRAMEWORKS",
  columns: "AI_CHAT/TOOL_RESOURCE/COLUMNS",
}

/** Result keys holding the row count for the list the tool returned. */
const COUNT_KEYS = [
  "total_matching",
  "total",
  "count",
  "matched",
  "total_count",
] as const

/**
 * Input keys worth showing as a chip — what the user would recognise as "what
 * it looked for". An allowlist rather than "every scalar": tool input also
 * carries ids, limits, offsets and flags, which are noise on screen and, for
 * ids, information the user has no way to act on.
 */
const CHIP_KEYS = [
  "q",
  "query",
  "search",
  "name",
  "supplier",
  "supplier_name",
  "article_number",
  "declaration_number",
  "substance",
  "cas_number",
  "regulation",
  "file_name",
  "question",
  // `load_tools`' input: the tool names it is fetching schemas for. A LIST,
  // unlike every other entry here — see the array branch in `presentTool`.
  "tools",
] as const

/** Tools whose `result` describes the model's own plumbing rather than the
 * user's data, so no detail line is better than theirs. */
const SUMMARY_IS_FOR_THE_MODEL: ReadonlySet<string> = new Set(["load_tools"])

const MAX_CHIPS = 3
// Only a bound on the value. The chip truncates in CSS and shows this value on
// hover, so it has to hold a whole search query.
const MAX_CHIP_CHARS = 200
const MAX_DETAIL_CHARS = 160

/** Cap a string for display and mark the cut, so a clipped value is not read
 * as the whole value. Applies to everything from `input` (model-authored) and
 * `result` (database-authored) — neither is bounded at the source. */
function clip(value: string, maximum: number): string {
  const collapsed = value.replace(/\s+/g, " ").trim()
  return collapsed.length <= maximum
    ? collapsed
    : `${collapsed.slice(0, maximum)}…`
}

/** First array-valued entry, which by convention is the list the tool
 * returned — the key is domain-named per tool (`declarations`, `articles`,
 * `items`, …) rather than uniform, so it is found by shape, not by name. */
function firstList(
  result: Record<string, unknown>,
): { key: string; length: number } | undefined {
  for (const [key, value] of Object.entries(result)) {
    if (Array.isArray(value)) {
      return { key, length: value.length }
    }
  }
  return undefined
}

function countOf(result: Record<string, unknown>): number | undefined {
  for (const key of COUNT_KEYS) {
    const value = result[key]
    if (typeof value === "number" && Number.isFinite(value)) {
      return value
    }
  }
  return undefined
}

/** `list_supplier_declarations` -> "List supplier declarations". The fallback
 * for a tool with no label entry: a readable name beats the raw identifier,
 * and beats a generic "Working on it" that says nothing about which step
 * this was. */
export function humanizeToolName(tool: string): string {
  const words = tool.replace(/[_-]+/g, " ").trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : tool
}

/** Params for the two derivable detail shapes below — always plain values,
 * `resource` still raw (untranslated), resolved by the caller via
 * `RESOURCE_LABEL_KEYS`. */
export interface ToolDetailParams {
  shown?: number
  total?: number
  count?: number
  resource?: string
}

export interface ToolPresentation {
  /** Translation key for the label, when the tool has one. */
  labelKey?: TranslationKeys
  /** Fallback label for a tool with no translation entry. */
  labelFallback: string
  /** One line about what came back, already clipped. Omitted when there is
   * nothing worth saying. Prose (backend `summary`/`error`, or a derived
   * string) — see `detailKey` for when this is a translatable fallback
   * rather than the only version. */
  detail?: string
  /**
   * Set only for the two shapes fully derivable from structured fields (a
   * paged list, a bare count) — never for `result.summary`/`result.error`,
   * which are free backend prose with no small fixed set of strings to key.
   * Preferred over `detail` when both are present; `detail` still gets
   * computed alongside it as the fallback for a client that predates this
   * key (see `dev-anti-patterns.md#backend-prose-key-plus-params`).
   */
  detailKey?: TranslationKeys
  detailParams?: ToolDetailParams
  /** What the tool was asked to look for, already clipped. */
  chips: string[]
}

/**
 * Turn one tool call's raw `input`/`result` into something showable.
 *
 * A tool result is OUR API's JSON handed to the model as data — it is not
 * model prose and cannot be shown raw. Rather than a formatter per tool
 * (there are ~60, and a new one would show nothing), this reads the
 * conventions the handlers already follow: a `summary` string several of them
 * write for the model, a domain-named list, and a count key.
 */
export function presentTool(
  tool: string,
  input?: Record<string, unknown>,
  result?: Record<string, unknown>,
): ToolPresentation {
  const presentation: ToolPresentation = {
    labelKey: TOOL_LABEL_KEYS[tool],
    labelFallback: humanizeToolName(tool),
    chips: [],
  }

  if (input) {
    for (const key of CHIP_KEYS) {
      if (presentation.chips.length >= MAX_CHIPS) break
      const value = input[key]
      if (typeof value === "string" && value.trim()) {
        presentation.chips.push(clip(value, MAX_CHIP_CHARS))
      } else if (typeof value === "number") {
        presentation.chips.push(String(value))
      } else if (Array.isArray(value)) {
        for (const entry of value) {
          if (presentation.chips.length >= MAX_CHIPS) break
          if (typeof entry !== "string" || !entry.trim()) continue
          // `tools` holds tool NAMES, which are raw identifiers; every other
          // list would hold values the user typed or recognises already.
          presentation.chips.push(
            clip(
              key === "tools" ? humanizeToolName(entry) : entry,
              MAX_CHIP_CHARS,
            ),
          )
        }
      }
    }
  }

  if (!result) {
    return presentation
  }

  const truncated =
    typeof result.truncated === "string" ? result.truncated : undefined

  // The failure reason first: a tool that errored has no list to count, and
  // "0 declarations" would read as an empty result rather than a failure.
  if (typeof result.error === "string" && result.error.trim()) {
    presentation.detail = clip(result.error, MAX_DETAIL_CHARS)
    return presentation
  }

  // A handful of tools write a `summary` aimed squarely at the MODEL rather
  // than at a reader — `load_tools` returns "Loaded 1 tool(s). Their
  // parameters are now available — call them now.", which is an instruction,
  // not a description of anything the user cares about. For those, the label
  // alone says enough.
  if (SUMMARY_IS_FOR_THE_MODEL.has(tool)) {
    return presentation
  }

  // Written by the handler for the model to read, so it is already a human
  // sentence about this exact result — better than anything derivable.
  if (typeof result.summary === "string" && result.summary.trim()) {
    presentation.detail = clip(
      truncated ? `${result.summary} ${truncated}` : result.summary,
      MAX_DETAIL_CHARS,
    )
    return presentation
  }

  const list = firstList(result)
  const count = countOf(result)

  if (list && count !== undefined && count > list.length) {
    presentation.detail = clip(
      `${list.length} / ${count} ${humanizeKey(list.key)}`,
      MAX_DETAIL_CHARS,
    )
    presentation.detailKey = "AI_CHAT/TOOL_DETAIL/PAGE_OF_TOTAL"
    presentation.detailParams = {
      shown: list.length,
      total: count,
      resource: list.key,
    }
  } else if (list) {
    presentation.detail = clip(
      `${list.length} ${humanizeKey(list.key)}`,
      MAX_DETAIL_CHARS,
    )
    presentation.detailKey = "AI_CHAT/TOOL_DETAIL/COUNT_OF"
    presentation.detailParams = { count: list.length, resource: list.key }
  } else if (count !== undefined) {
    // A bare number with no resource noun attached — nothing textual to
    // translate, so no key: the digits read the same in every language.
    presentation.detail = clip(String(count), MAX_DETAIL_CHARS)
  }

  // `truncated` is itself free backend prose ("Showing the 4 most recent of
  // 19 matches.") — appended to whichever detail won above, English either
  // way. Out of scope for this pass: it is the rarer case, not the "most
  // tools" common path the key+params split targets.
  if (presentation.detail && truncated) {
    presentation.detail = clip(
      `${presentation.detail} — ${truncated}`,
      MAX_DETAIL_CHARS,
    )
  }

  return presentation
}

/** `total_matching` / `supplier_declarations` -> readable words. Fallback for
 * a resource this build has no `RESOURCE_LABEL_KEYS` entry for. */
function humanizeKey(key: string): string {
  return key.replace(/[_-]+/g, " ").trim()
}

/** Resolve a raw resource name (e.g. "declarations") to display text — the
 * same key-with-fallback shape as a tool's own `labelKey`/`labelFallback`. */
export function resourceLabel(
  resource: string,
  translate: (key: TranslationKeys) => string,
): string {
  const key = RESOURCE_LABEL_KEYS[resource]
  return key ? translate(key) : humanizeKey(resource)
}
